import { Building2, FileCheck, Mail, Phone, User } from "lucide-react";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { PageHeader } from "@/components/shared/page-header";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useCompany } from "@/hooks/use-companies";
import { useComplianceDocuments } from "@/hooks/use-compliance";
import { useContacts } from "@/hooks/use-contacts";
import { useSession } from "@/lib/auth-client";

export function MyCompanyPage() {
  const { data: session, isPending: sessionLoading } = useSession();
  const companyId = (session?.user as Record<string, unknown> | undefined)?.companyId as
    | string
    | undefined;
  const { data: company, isLoading, isError, error, refetch } = useCompany(companyId ?? "");

  if (sessionLoading || isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!companyId) {
    return (
      <EmptyState
        icon={Building2}
        title="No company assigned"
        description="Your account is not linked to a company. Please contact your administrator."
      />
    );
  }

  if (isError) {
    return (
      <ErrorState message={error?.message ?? "Failed to load company"} onRetry={() => refetch()} />
    );
  }

  if (!company) {
    return (
      <EmptyState
        icon={Building2}
        title="Company not found"
        description="Your assigned company could not be found."
      />
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader title={company.companyName} description="Your company portal" />

      <Tabs defaultValue="info">
        <TabsList>
          <TabsTrigger value="info">Company Info</TabsTrigger>
          <TabsTrigger value="compliance">Compliance</TabsTrigger>
          <TabsTrigger value="contacts">Contacts</TabsTrigger>
        </TabsList>

        <TabsContent value="info" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Company Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">Name</dt>
                  <dd className="mt-1 text-sm">{company.companyName}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">Type</dt>
                  <dd className="mt-1 text-sm">
                    <Badge variant="outline">{company.type ?? "N/A"}</Badge>
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">Trade</dt>
                  <dd className="mt-1 text-sm">{company.trade ?? "N/A"}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">Status</dt>
                  <dd className="mt-1 text-sm">{company.status ?? "N/A"}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">Contact Person</dt>
                  <dd className="mt-1 text-sm flex items-center gap-1">
                    <User className="h-3.5 w-3.5 text-muted-foreground" />
                    {company.contactPerson ?? "N/A"}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">Email</dt>
                  <dd className="mt-1 text-sm flex items-center gap-1">
                    <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                    {company.email ?? "N/A"}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">Phone</dt>
                  <dd className="mt-1 text-sm flex items-center gap-1">
                    <Phone className="h-3.5 w-3.5 text-muted-foreground" />
                    {company.phone ?? "N/A"}
                  </dd>
                </div>
              </dl>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="compliance" className="mt-4">
          <MyCompanyCompliance companyId={companyId} />
        </TabsContent>

        <TabsContent value="contacts" className="mt-4">
          <MyCompanyContacts companyId={companyId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function MyCompanyCompliance({ companyId }: { companyId: string }) {
  const { data, isLoading, isError, error, refetch } = useComplianceDocuments(companyId);

  if (isLoading) {
    return <Skeleton className="h-48 w-full" />;
  }

  if (isError) {
    return (
      <ErrorState
        message={error?.message ?? "Failed to load compliance documents"}
        onRetry={() => refetch()}
      />
    );
  }

  const items = data ?? [];

  if (items.length === 0) {
    return (
      <EmptyState
        icon={FileCheck}
        title="No compliance documents"
        description="No compliance documents found for your company."
      />
    );
  }

  return (
    <Card>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Document Type</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Expiration Date</TableHead>
            <TableHead>Notes</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((doc) => (
            <TableRow key={doc.id}>
              <TableCell className="font-medium">{doc.documentType ?? "--"}</TableCell>
              <TableCell>
                <Badge variant={doc.status === "valid" ? "default" : "secondary"}>
                  {doc.status ?? "--"}
                </Badge>
              </TableCell>
              <TableCell>{doc.expiryDate ?? "--"}</TableCell>
              <TableCell className="max-w-xs truncate">{doc.notes ?? "--"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

function MyCompanyContacts({ companyId }: { companyId: string }) {
  const { data, isLoading, isError, error, refetch } = useContacts();

  if (isLoading) {
    return <Skeleton className="h-48 w-full" />;
  }

  if (isError) {
    return (
      <ErrorState message={error?.message ?? "Failed to load contacts"} onRetry={() => refetch()} />
    );
  }

  const items = (data ?? []).filter((c) => c.companyId === companyId);

  if (items.length === 0) {
    return (
      <EmptyState
        icon={User}
        title="No contacts"
        description="No contacts linked to your company."
      />
    );
  }

  return (
    <Card>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Title</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Phone</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((contact) => (
            <TableRow key={contact.id}>
              <TableCell className="font-medium">
                {[contact.firstName, contact.lastName].filter(Boolean).join(" ") || "--"}
              </TableCell>
              <TableCell>{contact.title ?? "--"}</TableCell>
              <TableCell>{contact.email ?? "--"}</TableCell>
              <TableCell>{contact.phone ?? "--"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}
